
import React from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface PrivacyToggleProps {
  isPublic: boolean;
  onChange: (value: boolean) => void;
  label?: string;
  className?: string;
}

const PrivacyToggle: React.FC<PrivacyToggleProps> = ({ 
  isPublic, 
  onChange, 
  label,
  className = ""
}) => {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {label && <Label className="text-sm text-muted-foreground">{label}</Label>}
      
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-2">
              <Switch
                checked={isPublic}
                onCheckedChange={onChange}
                className="data-[state=checked]:bg-primary"
              />
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.2 }}
                key={isPublic ? 'public' : 'private'}
                className={`flex items-center gap-1 ${isPublic ? 'text-primary' : 'text-muted-foreground'}`}
              >
                {isPublic ? (
                  <>
                    <Eye size={14} />
                    <span className="text-xs font-medium">Public</span>
                  </>
                ) : (
                  <>
                    <Lock size={14} />
                    <span className="text-xs font-medium">Private</span>
                  </>
                )}
              </motion.div>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {isPublic 
                ? "This information is visible to anyone viewing your profile" 
                : "This information is private and only visible when you grant access"}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export default PrivacyToggle;
